import { useParams } from '@tanstack/react-router';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import type { TOCChild, TOCItem } from '~/common/types';
import Icon from '~/components2/Icon';
import { useCustomDataRememberVisibility } from '~/hooks/useCustomDataRememberVisibility';
import rootStore from '~/store';
import layerStore, { CUSTOM_DATA_SECTION_ID } from '~/store2/layers';
import {
  CUSTOM_DATA_SECTION_LABEL,
  type CustomDataTOCRow,
  customDataUploadKey,
  filterCustomDataTOCRows,
  seedCustomDataSectionDefaultExpansion,
  toggleCustomDataLeaf,
  toggleCustomDataSection,
  toggleCustomDataSectionExpanded,
  toggleCustomDataUpload,
  toggleCustomDataUploadExpandedFromRow,
} from './customDataTOC.handlers';
import { PickerCheckbox } from './PickerCheckbox';
import { toPanelId } from './panelIds';
import { collectLeaves } from './sharedUtils';

// Picker section for the project-scoped "Custom Data" group
// (CG-521 uploads). The document-driven picker renders only catalog + document nodes, and
// custom-data ids are per-project, so this section is custom-handled outside the layerConfigStore
// facade (see customDataTOC.handlers.ts for the write-path invariants). No pin / "Only" / zoom
// commands — those facade paths are document-affecting.
export default function CustomDataTOC({ searchTerm = '' }: { searchTerm?: string }) {
  const { customDataTOCGroup } = useSnapshot(layerStore) as typeof layerStore;
  if (customDataTOCGroup.length === 0) return null;
  return <CustomDataTOCContent items={customDataTOCGroup} searchTerm={searchTerm} />;
}

function CustomDataTOCContent({ items, searchTerm }: { items: readonly TOCItem[]; searchTerm: string }) {
  const { visibleLayerIds, expandedSections } = useSnapshot(layerStore) as typeof layerStore;
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  // Undefined unless visibility is remembered, which is what keeps the handlers from writing.
  // Distinct from the `projectId` passed as analytics context, which is unconditional.
  const persistProjectId = useCustomDataRememberVisibility() ? projectId : undefined;

  // Default the section expanded once per session (the TOC panel unmounts on close, so a
  // per-mount seed would undo a user collapse on every reopen). Session-only: the
  // expandedSections localStorage writeback is suppressed.
  useEffect(() => {
    seedCustomDataSectionDefaultExpansion();
  }, []);

  // Header state covers the whole group: search filters what renders, never what a checkbox
  // reports or a bulk toggle writes (legacy picker parity).
  const leaves = collectLeaves(items);
  const visibleCount = leaves.filter((c) => visibleLayerIds.has(c.id)).length;
  const sectionCheckedState: boolean | 'indeterminate' =
    visibleCount === 0 ? false : visibleCount === leaves.length ? true : 'indeterminate';

  const isSearching = !isEmpty(searchTerm);
  const rows = filterCustomDataTOCRows(items, searchTerm);
  if (rows.length === 0) return null;

  // Search force-expands at render time only — expandedSections feeds the debounced document
  // sync, so a transient search expansion must never be written into it.
  const isExpanded = expandedSections.has(CUSTOM_DATA_SECTION_ID) || isSearching;
  const panelId = toPanelId('toc', CUSTOM_DATA_SECTION_ID);

  const renderLeaf = (leaf: TOCChild) => (
    <div
      key={leaf.id}
      data-testid={`toc-layer-${leaf.id}`}
      className="group relative my-1 ml-7 flex min-h-[1.75rem] flex-row items-center justify-between space-x-2"
    >
      <div className="grow">
        <PickerCheckbox
          data-testid={`toc-layer-toggle-${leaf.id}`}
          checked={visibleLayerIds.has(leaf.id)}
          onCheckedChange={(value) => {
            if (value === 'indeterminate') return;
            toggleCustomDataLeaf(
              leaf.id,
              value,
              { projectId, organizationId: rootStore.currentOrganizationId || undefined },
              persistProjectId,
            );
          }}
          label={leaf.label}
        />
      </div>
    </div>
  );

  const renderUpload = ({ parent, children }: Extract<CustomDataTOCRow, { kind: 'upload' }>) => {
    const sectionKey = customDataUploadKey(parent);
    const uploadLeaves = collectLeaves(parent.children);
    const uploadVisibleCount = uploadLeaves.filter((leaf) => visibleLayerIds.has(leaf.id)).length;
    const uploadCheckedState: boolean | 'indeterminate' =
      uploadVisibleCount === 0 ? false : uploadVisibleCount === uploadLeaves.length ? true : 'indeterminate';
    const isUploadExpanded = expandedSections.has(sectionKey) || isSearching;
    const panelId = toPanelId('toc', sectionKey);

    return (
      <div key={sectionKey} data-testid={`toc-section-${sectionKey}`} className="my-2 px-1">
        <div className="group flex cursor-pointer items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              data-testid={`toc-section-expand-${sectionKey}`}
              className="inline-flex appearance-none border-0 bg-transparent p-0"
              onClick={() => toggleCustomDataUploadExpandedFromRow(parent, isUploadExpanded, isSearching)}
              aria-label={parent.label}
              aria-expanded={isUploadExpanded}
              aria-controls={isUploadExpanded ? panelId : undefined}
            >
              <Icon icon={isUploadExpanded ? 'chevronDown' : 'chevronRight'} />
            </button>

            <PickerCheckbox
              data-testid={`toc-section-toggle-${sectionKey}`}
              checked={uploadCheckedState}
              onCheckedChange={(value) => {
                if (value === 'indeterminate') return;
                toggleCustomDataUpload(parent, value, persistProjectId);
              }}
              label={parent.label}
            />
          </div>
        </div>

        {isUploadExpanded && (
          <div id={panelId} className="my-2 ml-2.5">
            {children.map(renderLeaf)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid={`toc-section-${CUSTOM_DATA_SECTION_ID}`} className="my-2 px-1">
      <div className="group flex cursor-pointer items-center justify-between space-x-2">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            data-testid={`toc-section-expand-${CUSTOM_DATA_SECTION_ID}`}
            className="inline-flex appearance-none border-0 bg-transparent p-0"
            onClick={() => toggleCustomDataSectionExpanded(!isExpanded, isSearching)}
            aria-label={CUSTOM_DATA_SECTION_LABEL}
            aria-expanded={isExpanded}
            aria-controls={isExpanded ? panelId : undefined}
          >
            <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} />
          </button>

          <PickerCheckbox
            data-testid={`toc-section-toggle-${CUSTOM_DATA_SECTION_ID}`}
            checked={sectionCheckedState}
            onCheckedChange={(value) => {
              if (value === 'indeterminate') return;
              toggleCustomDataSection(leaves, value, persistProjectId);
            }}
            label={CUSTOM_DATA_SECTION_LABEL}
          />
        </div>
      </div>

      {isExpanded && (
        <div id={panelId} className="my-2 ml-2.5">
          {rows.map((row) => (row.kind === 'upload' ? renderUpload(row) : renderLeaf(row.leaf)))}
        </div>
      )}
    </div>
  );
}
